import { AppShell as MantineAppShell, Stack, VisuallyHidden } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SharedUi } from '@shared';

/** What the region says while it waits. A key, never a sentence (`rules/i18n.mdc` §1). */
const LOADING_MESSAGE_KEY = 'common.loading';

/**
 * Геометрия оболочки, повторённая здесь намеренно.
 *
 * Те же три числа объявлены в `widgets/app-shell`, и разделить их нечем: они его внутренние
 * константы, а этот экран рисуется **до** того, как оболочка вообще может быть смонтирована — она
 * живёт в защищённой ветке роутера, которого ещё нет. Собрать их в общий модуль значило бы завести
 * `shared`-модуль ради трёх чисел и связать `app` с внутренностями виджета; расхождение же стоит
 * дёшево — сдвиг каркаса на первом кадре, а не сломанный экран.
 */
const HEADER_HEIGHT = 56;
const NAVBAR_WIDTH = 260;
const NAVBAR_BREAKPOINT = 'sm';

/**
 * The one screen shown before the client knows who it is.
 *
 * It exists because of the frame that would otherwise be there. Between the first paint and the
 * answer of `POST /auth/refresh` the session is `unknown`; render the router in that gap and a
 * signed-in user reloading the page sees the login form flash before being sent back — the failure
 * this screen exists to prevent: `/login` must not render for a single frame. The session bootstrap
 * behind that rule is EPIC-014. Rendering nothing at all would be the other half of the same
 * mistake: a blank page reads as broken, and says nothing to anybody who is not looking at it.
 *
 * **Что здесь рисуется — каркас оболочки, а не три полосы по центру пустого экрана.** До
 * STORY-043-01 первый кадр вкладки состоял из полос во всю ширину: ни шапки, ни навигации, ничего,
 * по чему можно узнать приложение, — и выглядел он как сломанная страница ровно в тот момент, когда
 * его видят впервые. Разделение простое: **геометрия оболочки известна до запроса** и потому
 * рисуется сразу, а из сети приезжает только содержимое — под ним и стоит скелетон. Каркас — это
 * настоящий `AppShell` с теми же размерами, а не его имитация из своих отступов: одна разметка на
 * оба состояния, поэтому появление роутера ничего не двигает.
 *
 * Шапка и навигация — пустые прямоугольники и помечены `aria-hidden`: это ориентиры для глаза, а
 * не ориентиры страницы, и объявлять пустую навигацию навигацией значило бы отправить читателя
 * скринридера в неё за содержимым, которого там нет. Занятой объявлена одна область — та, где
 * стоит скелетон (`rules/a11y.mdc` §16), потому что каркас ничего не ждёт. Сообщение «загрузка»
 * визуально скрыто, а не отсутствует: это ровно то, чего не вывести из вида пустого экрана.
 *
 * **The key was rendered raw until 2026-08-19**, so the first frame of every tab announced
 * `common.loading` to a screen reader — the only sentence this screen has, and it was not a
 * sentence. Nothing caught it: the whole client suite runs in `cimode`, where `t(key)` returns the
 * key and a component that forgets `t()` renders identically to one that remembers, while
 * `i18next/no-literal-string` sees a constant holding a key rather than a literal in the markup.
 * The same hole `ErrorState` fell through; `test/i18n/pseudo-locale.test.tsx` now covers this
 * component too.
 */
export function AppLoading() {
  const { t } = useTranslation();

  return (
    <MantineAppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={{
        width: NAVBAR_WIDTH,
        breakpoint: NAVBAR_BREAKPOINT,
        // Как в оболочке: ниже брейкпоинта навигация — дровер, а рельса свёрнута.
        collapsed: { mobile: true },
      }}
      padding="md"
    >
      <MantineAppShell.Header aria-hidden="true" data-testid="app-loading-header" />
      <MantineAppShell.Navbar aria-hidden="true" data-testid="app-loading-nav" />

      <MantineAppShell.Main>
        <Stack gap="sm">
          <VisuallyHidden aria-live="polite" role="status">
            {t(LOADING_MESSAGE_KEY)}
          </VisuallyHidden>
          <SharedUi.TextSkeleton lines={3} />
        </Stack>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
